import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { formatPrice } from '../../../utils/format';

interface VenteEditModalProps {
  isOpen: boolean;
  vente: any; // the sale object from history
  onClose: () => void;
  onSaved: () => void;
}

interface EditItem {
  tissu_id: number;
  tissu_nom: string;
  quantite: number;
  prix_unitaire: number;
  total_ligne: number;
  max_stock: number;
}

export default function VenteEditModal({ isOpen, vente, onClose, onSaved }: VenteEditModalProps) {
  const [items, setItems] = useState<EditItem[]>([]);
  const [clientNom, setClientNom] = useState('');
  const [remise, setRemise] = useState(0);
  const [acompte, setAcompte] = useState(0);
  const [modePaiement, setModePaiement] = useState('Espèces');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [stock, setStock] = useState<any[]>([]);
  const [showAddTissu, setShowAddTissu] = useState(false);
  const [searchTissu, setSearchTissu] = useState('');

  useEffect(() => {
    if (!isOpen || !vente) return;

    // Populate from existing sale data
    setClientNom(vente.client_nom || '');
    setRemise(vente.remise || 0);
    setAcompte(vente.acompte || 0);
    setModePaiement(vente.mode_paiement || 'Espèces');
    setError('');

    // Map articles to editable items
    if (vente.articles && vente.articles.length > 0) {
      setItems(vente.articles.map((a: any) => ({
        tissu_id: a.tissu_id,
        tissu_nom: a.tissu_nom || a.nom || 'Tissu',
        quantite: Number(a.quantite),
        prix_unitaire: Number(a.prix_unitaire),
        total_ligne: Number(a.total_ligne || a.total || 0),
        max_stock: 9999 // will be refined when stock loads
      })));
    } else {
      // Fallback: single-article sale (old format)
      setItems([{
        tissu_id: vente.tissu_id || 0,
        tissu_nom: vente.tissu_nom || 'Tissu',
        quantite: Number(vente.quantite || 0),
        prix_unitaire: Number(vente.prix_unitaire || 0),
        total_ligne: Number(vente.total || 0),
        max_stock: 9999
      }]);
    }

    // Load stock for adding new items
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token) {
      api.getStocks(token).then((s: any) => {
        if (Array.isArray(s)) setStock(s);
      }).catch(() => {});
    }
  }, [isOpen, vente]);

  const updateItem = (idx: number, field: string, value: number) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [field]: value };
      if (field === 'quantite' || field === 'prix_unitaire') {
        updated.total_ligne = updated.quantite * updated.prix_unitaire;
      }
      return updated;
    }));
  };

  const removeItem = (idx: number) => {
    if (items.length <= 1) { setError("La vente doit contenir au moins 1 article."); return; }
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const addTissu = (tissu: any) => {
    // Check no duplicate
    if (items.some(it => it.tissu_id === tissu.id)) {
      setError("Ce tissu est déjà dans la vente. Modifiez la quantité.");
      return;
    }
    setItems(prev => [...prev, {
      tissu_id: tissu.id,
      tissu_nom: tissu.nom,
      quantite: 1,
      prix_unitaire: Number(tissu.prix_unitaire || 0),
      total_ligne: Number(tissu.prix_unitaire || 0),
      max_stock: Number(tissu.quantite_stock || 9999)
    }]);
    setShowAddTissu(false);
    setSearchTissu('');
  };

  const totalHT = items.reduce((s, it) => s + it.total_ligne, 0);
  const totalNet = Math.max(0, totalHT - remise);
  const reste = Math.max(0, totalNet - acompte);

  const handleSave = async () => {
    if (items.length === 0) { setError("Ajoutez au moins un article."); return; }
    if (items.some(it => it.quantite <= 0)) { setError("Les quantités doivent être positives."); return; }

    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('atelier_bassoum_token') || '';
      await api.updateVente(token, vente.id, {
        client_nom: clientNom,
        client_id: vente.client_id || null,
        remise,
        acompte,
        mode_paiement: modePaiement,
        items: items.map(it => ({
          tissu_id: it.tissu_id,
          quantite: it.quantite,
          prix_unitaire: it.prix_unitaire,
          total_ligne: it.total_ligne
        }))
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || "Erreur lors de la modification.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const filteredStock = stock.filter(t =>
    t.nom?.toLowerCase().includes(searchTissu.toLowerCase()) &&
    !items.some(it => it.tissu_id === t.id)
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-foret-50 to-white">
          <div>
            <h2 className="font-playfair font-bold text-lg text-gray-900 flex items-center gap-2">
              <i className="ri-edit-line text-foret-600" />
              Modifier la vente
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Code : {vente?.code || `#${vente?.id}`}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Client */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 block">Client</label>
            <input
              type="text"
              value={clientNom}
              onChange={e => setClientNom(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-500/30 focus:border-foret-500 transition-all"
              placeholder="Nom du client"
            />
          </div>

          {/* Articles */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Articles</label>
              <button
                onClick={() => setShowAddTissu(!showAddTissu)}
                className="text-xs text-foret-600 font-bold hover:text-foret-700 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-add-circle-line" /> Ajouter un tissu
              </button>
            </div>

            {/* Add tissu dropdown */}
            {showAddTissu && (
              <div className="mb-3 bg-gray-50 rounded-xl p-3 border border-gray-100 animate-in fade-in slide-in-from-top-2 duration-200">
                <input
                  type="text"
                  value={searchTissu}
                  onChange={e => setSearchTissu(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-foret-500/30"
                  placeholder="Rechercher un tissu..."
                  autoFocus
                />
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {filteredStock.slice(0, 8).map(t => (
                    <button
                      key={t.id}
                      onClick={() => addTissu(t)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-foret-50 transition-colors text-sm flex justify-between items-center cursor-pointer"
                    >
                      <span className="font-semibold text-gray-800">{t.nom}</span>
                      <span className="text-xs text-gray-400">{t.quantite_stock} {t.unite || 'm'} · {formatPrice(t.prix_unitaire)}</span>
                    </button>
                  ))}
                  {filteredStock.length === 0 && <p className="text-xs text-gray-400 text-center py-2">Aucun tissu trouvé</p>}
                </div>
              </div>
            )}

            {/* Items list */}
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:border-foret-200 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      <i className="ri-palette-line text-foret-500" />
                      {it.tissu_nom}
                    </p>
                    <button
                      onClick={() => removeItem(idx)}
                      className="text-gray-300 hover:text-red-500 transition-colors cursor-pointer p-1"
                      title="Retirer"
                    >
                      <i className="ri-close-circle-line" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-400 font-bold uppercase">Quantité (m)</label>
                      <input
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={it.quantite}
                        onChange={e => updateItem(idx, 'quantite', parseFloat(e.target.value) || 0)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-foret-500/30"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 font-bold uppercase">P.U. (FCFA)</label>
                      <input
                        type="number"
                        min={0}
                        value={it.prix_unitaire}
                        onChange={e => updateItem(idx, 'prix_unitaire', parseFloat(e.target.value) || 0)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-foret-500/30"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 font-bold uppercase">Total ligne</label>
                      <p className="text-sm font-black text-gray-900 mt-1.5">{formatPrice(it.total_ligne)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Financial section */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-gray-400 font-bold uppercase">Remise globale</label>
              <input
                type="number"
                min={0}
                value={remise}
                onChange={e => setRemise(parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-foret-500/30"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 font-bold uppercase">Acompte versé</label>
              <input
                type="number"
                min={0}
                value={acompte}
                onChange={e => setAcompte(parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-foret-500/30"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] text-gray-400 font-bold uppercase">Mode de paiement</label>
            <select
              value={modePaiement}
              onChange={e => setModePaiement(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-foret-500/30"
            >
              <option>Espèces</option>
              <option>Mobile Money</option>
              <option>Virement</option>
              <option>Carte</option>
            </select>
          </div>

          {/* Summary */}
          <div className="bg-foret-50 rounded-xl p-4 border border-foret-100">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Total HT</span>
              <span className="font-bold text-gray-800">{formatPrice(totalHT)}</span>
            </div>
            {remise > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Remise</span>
                <span className="font-bold text-red-500">-{formatPrice(remise)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm mb-1 pt-1 border-t border-foret-200/50">
              <span className="font-bold text-gray-700">Total Net</span>
              <span className="font-black text-gray-900">{formatPrice(totalNet)}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Acompte</span>
              <span className="font-bold text-foret-700">{formatPrice(acompte)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-bold text-gray-700">Reste</span>
              <span className={`font-black ${reste > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatPrice(reste)}</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 text-xs font-bold p-3 rounded-xl border border-red-100 flex items-center gap-2">
              <i className="ri-error-warning-line" /> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 font-semibold cursor-pointer transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-foret-500 hover:bg-foret-600 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-colors shadow-lg shadow-foret-100"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <i className="ri-save-line" />
            )}
            {saving ? 'Enregistrement...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}
